import type { CompassModalProps } from "@circle-react/components/SettingsApp/CompassCompat/modalHelpers";
import { PROFILE_PAGES } from "@circle-react/helpers/memberHelper";
import { useProfileInfo } from "@circle-react/hooks/profile/useProfileInfo";
import { Modal } from "@circleco/compass/components/Modal";
import { useLocation } from "@tanstack/react-router";
import { Suspense, useEffect, useRef } from "react";

import { t } from "@/i18n-js/instance";
import { lazyWithRetry } from "@/react/helpers/lazyRetry";
import { useChatUIStore } from "@/react/hooks/chatsV2/store";
import { PageLoader } from "@/react/pages/PageLoader";

const ProfileMessaging = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ProfileMessagingV4" */ "@circle-react/components/ProfileModalV4/ProfileMessaging"
  ).then(module => ({ default: module.ProfileMessaging })),
);

const ProfileV4 = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ProfileV4" */ "@circle-react/components/ProfileModalV4/Profile"
  ).then(module => ({ default: module.ProfileV4 })),
);

const pageParams = {
  page_name: PROFILE_PAGES.PROFILE_VIEW,
};

interface ProfileModalV4ComponentProps extends CompassModalProps {
  memberPublicId: string;
  params?: {
    shouldOpenInNewTab?: boolean;
    recommendation_id?: number;
  };
}

export const ProfileModalV4Component = ({
  memberPublicId,
  params,
  open,
  onOpenChange,
}: ProfileModalV4ComponentProps) => {
  const pathname = useLocation({ select: location => location.pathname });
  const pathnameOnOpenRef = useRef(pathname);
  const { liveChatRoomUuid, setLiveChatRoomUuid } = useChatUIStore();
  const {
    getProfileInfoQuery: {
      data: member,
      isPending,
      refetch: refetchProfileInfo,
    },
  } = useProfileInfo({
    publicId: memberPublicId,
    params: pageParams,
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setLiveChatRoomUuid(null);
    }
    onOpenChange(next);
  };

  // Track the pathname when the modal opens so we can detect in-modal
  // navigation (e.g. clicking a post inside ProfilePosts).
  useEffect(() => {
    if (open) {
      pathnameOnOpenRef.current = pathname;
    }
  }, [open]);

  // Close the modal when the route changes while it's open.
  useEffect(() => {
    if (open && pathname !== pathnameOnOpenRef.current) {
      handleOpenChange(false);
    }
  }, [open, pathname]);

  const recommendationId = params?.recommendation_id ?? undefined;

  const title = liveChatRoomUuid ? (member?.name ?? "") : t("profile");

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} size="full-screen">
      <Modal.Content>
        <Modal.Header title={title} />
        {/* The DM view owns its scroll region (`ScrollWrapperV4` sizes it with
            `grow basis-0`), so it needs the bounded flex body rather than the
            default ScrollArea, which collapsed the messages and composer. */}
        <Modal.Body customLayout={Boolean(liveChatRoomUuid)}>
          <Suspense fallback={<PageLoader />}>
            {liveChatRoomUuid ? (
              <ProfileMessaging uuid={liveChatRoomUuid} />
            ) : (
              <div className="flex min-h-full w-full flex-col gap-6 md:flex-row xl:mx-auto xl:max-w-7xl">
                <ProfileV4
                  member={member}
                  isLoading={isPending}
                  onClose={() => handleOpenChange(false)}
                  recommendationId={recommendationId}
                  refetchProfileInfo={refetchProfileInfo}
                />
              </div>
            )}
          </Suspense>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
